import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit, SecurityContext,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import {animate, style, transition, trigger} from "@angular/animations";
import {ComponentAsElement} from "../../../services/common/bootstrap/annotation/component-as-element";
import {Subject} from "rxjs";
import {ProductDetailsService} from "../../../services/product-details.service";
import {Product} from "../../../model/view/Product";
import {Location} from "@angular/common";
import {VariantType} from "../../../model/enums/VariantType.enum";
import {ProductType} from "../../../model/enums/ProductType.enum";
import {BreakpointsService} from "../../../services/breakpoints.service";
import {ActionMode, ProductDetailsMode, Section} from "../../../model/enums/ProductDetails.enum";
import {Site} from "../../../model/enums/Site.enum";
import {ProductAdd} from "../../../model/enums/ProductAdd";
import {NotificationService} from "../../../shared/notification/notification.service";
import {NotificationType} from "../../../model/view/Notification";
import {TranslationService} from "../../../services/translation.service";
import {
  DomSanitizer,
  SafeHtml
} from "@angular/platform-browser";


declare const configData;
@ComponentAsElement('v-product-details-element')
@Component({
  selector: 'v-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger(
      'enterAnimation', [
        transition(':enter', [
          style({opacity: 0}),
          animate('300ms', style({opacity: 1}))
        ])
      ],
    )
  ]
})
export class ProductDetailsComponent implements OnInit {
  @Input() initCode;
  @Input() isASM;
  @Input() site: Site;
  @Input() isAnonymous;
  @Input() isCustomerLoyaltySystem;
  mode: ProductDetailsMode = ProductDetailsMode.details;
  modes: typeof ProductDetailsMode = ProductDetailsMode;
  product$: Subject<Product> = new Subject;
  productAdded = null;
  loading = false;
  variantsType = VariantType;
  productType = ProductType;
  qty = 1;
  skipCheckingStock = false;
  actionMode: ActionMode = ActionMode.CART;
  actionM: typeof ActionMode = ActionMode;
  sites: typeof Site = Site;
  section: typeof Section = Section;
  @ViewChild('nav') nav: ElementRef;
  @ViewChild('top') top: ElementRef;
  @ViewChild('navMobile') navMobile: ElementRef;
  @ViewChild('menuMobile') menuMobile: ElementRef;
  @ViewChildren('sections', {read: ElementRef}) sections;
  navFixed = false;
  scrollPrev = 0;
  activeSection = Section.description;
  highlightSection = '';
  breakpoint$;
  screen: 'desktop'|'mobile';
  submitted = false;
  safeProductDescription: SafeHtml;
  noPicSrc = null;

  constructor(
    private productDetailsService: ProductDetailsService,
    private location: Location,
    private breakpointsService: BreakpointsService,
    private notificationService: NotificationService,
    private translate: TranslationService,
    private cd: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {

    this.initCode && this.loadProduct(this.initCode);
    this.breakpoint$ = this.breakpointsService.breakpoint$;
    this.breakpoint$.subscribe(breakpoint =>  {
      this.screen = breakpoint;
    });
    this.noPicSrc = this.site === Site.GRANDOPTICAL ? '/_ui/responsive/theme-grandoptical/images/nopic.jpg' : '/_ui/responsive/common/images/nopic.jpg'
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event) {
    if(!this.nav || !this.top || this.mode !== ProductDetailsMode.details) return;

    const scrollY = window.scrollY;

    const topScrollBottom = this.top.nativeElement.scrollTop + this.top.nativeElement.offsetHeight;
    scrollY > topScrollBottom
      ? this.navFixed = true : this.navFixed = false;
    this.scrollPrev = scrollY;
    this.sections.toArray().forEach((section) => section.nativeElement.offsetTop <= scrollY && (this.highlightSection = section.nativeElement.id));
  }

  scrollTo(element, event?, position = 'start') {
    const el = document.getElementById(element);
    this.screen === 'desktop' ? el.scrollIntoView({block: 'start', behavior: 'smooth'}) : this.activeSection = element;
    if(this.screen === 'mobile') {
      this.menuMobile.nativeElement.scrollTo({top: 0, behavior: 'smooth'})
    }
  }

  isAvailableOnline({purchasable, availableInCurrentChannel, onlineStockLevelStatus}) {
    return purchasable && availableInCurrentChannel && onlineStockLevelStatus != 'outOfStock';
  }

  getDefaultPictureUrl() {
    return this.noPicSrc;
  }

  loadProduct(code: string) {
    this.loading = true;
    this.product$.next(null)
    this.productDetailsService.getProduct(code).subscribe(product => {
      if (product.type === ProductType.FRAMES) {
        this.actionMode = ActionMode.LENSES
      }
      this.product$.next(product);
      this.safeProductDescription = this.sanitizer.sanitize(SecurityContext.HTML, this.sanitizer.bypassSecurityTrustHtml(product.description))
      this.loading = false;
      this.updateLocation(code);
      product.photoslurp && this.photoslurp(product.photoslurp);
      dataLayerProductView && dataLayerProductView(product, this.isAnonymous, this.isCustomerLoyaltySystem, this.isASM);
      insiderProductView && insiderProductView(product);
      googleStructuredData && googleStructuredData(product);
      this.fillConfigDataVmapp(product);
    }, error => this.translate.getWordByKey('productDetails.server.error').subscribe(message => this.notification(message)));
  }

  updateLocation(code: string) {
    const address = this.location.path().split('/');
    let newAddress = '';
    address[3] = code;
    newAddress = address.join('/')
    this.location.replaceState(newAddress)
  }

  switchAction(actionMode: ActionMode) {
    this.actionMode = actionMode;
  }

  switchMode(mode: ProductDetailsMode) {
    window.scroll(0,0);
    this.mode = mode;
  }

  primaryAction({code, name, action}) {
    action === ActionMode.CART ? this.addToCart(code) : this.openLensConfigurator({code, name})
  }

  addToCart(code: string) {
    const qty = this.qty;
    const skipCheckingStock = this.skipCheckingStock;
    const productCodePost = code;
    this.submitted = true;

    this.productDetailsService.addToCart({productCodePost, qty, skipCheckingStock}).subscribe((response: ProductAdd) => {
      const {code, product} = response;
      if(code === 'success') {
        const {code, name, brandName, price, insurancePrice, type} = product;
        dataLayerProductAdded && dataLayerProductAdded(product.code);

        if(type === this.productType.SUNGLASSES) {
          this.productAdded = {code, name, brandName, price, insurancePrice};
          this.switchMode(this.modes.added);
        }
        this.incraseMenuCartCounter();
      } else {
        this.notification(response.message || response.code);
      }
      this.submitted = false;
      this.cd.markForCheck();
    }, error => {
      this.translate.getWordByKey('productDetails.cart.error').subscribe(message => {
        this.notification(message);
        this.submitted = false;
      });
    });
  }

  incraseMenuCartCounter() {
    const menuCartCounters = document.querySelectorAll('.bpr_cart-items-count');
    const cartCouter = parseInt(menuCartCounters[0].innerHTML);

    menuCartCounters && [...menuCartCounters].forEach(menuCartCounter => menuCartCounter.innerHTML = `${cartCouter + 1}`);
  }

  notification(message: string, type: NotificationType = NotificationType.danger) {
    window.scroll({top:0, left:0, behavior: "smooth"});
    this.notificationService.notify({type, message});
  }

  addToCartLabel(type: string) {
    return type === this.productType.SUNGLASSES ? 'productDetails.cart.addToCart' : 'productDetails.cart.addOnlyFrames';
  }

  openLensConfigurator ({code, name}) {
    this.isASM === 'true' ? this.agePopup({code, name}) : window.location.href = `/okulary-${code}-${name}`;
  }

  agePopup(product) {
    window['openLensConfiguratorASMJs'](product);
  }

  cpButtonLabel(type: string) {
    return type === this.productType.SUNGLASSES ? 'productDetails.cart.chooseCorrectiveLenses' : 'productDetails.cart.chooseYourLensPower';
  }

  isAddToCartDisabled(product: Product) {
    return !product.purchasable || !product.availableInCurrentChannel || product.stockLevelStatus == 'outOfStock';
  }

  photoslurp(photoslurp) {
    const lang = window['ACC']['productDetailsPage']?.language || 'pl';
    window['photoSlurp'](photoslurp, lang, document.getElementById("main"));
  }

  fillConfigDataVmapp(product) {
    configData.products[0].upc = product.ean;
    configData.products[0].name = product.name;
    configData.products[0].code = product.code;
    configData.products[0].lensColorLabel = product.technicalData.lensColour;
    configData.products[0].frameColorLabel = product.technicalData.frameColour;

    if (product.mainImage) {
      configData.products[0].thumbnailUrl = product.mainImage;
    }

    if (product.brandName) {
      configData.products[0].brand.name = product.brandName;
    }

    const currentSize = product.dimensions.find(dimension => dimension.code === product.code);
    if (currentSize) {
      configData.products[0].size = currentSize.noseSize + ' ' + currentSize.lensWidth;
    }

    configData.prices[0].upc = product.ean;
  }

}

declare function dataLayerProductView(product:Product, isAnonymous:boolean, isCustomerLoyaltySystem:boolean, isASM:boolean): any;
declare function dataLayerProductAdded(code:String): any;
declare function insiderProductView(product:Product): any;
declare function googleStructuredData(product:Product): any;
